<template>
  <UModal v-if="rentUnit" v-model:open="modalIsOpen" fullscreen prevent-close>
    <template #body>
      <TContainer :mobile-full="true">
        <div class="flex flex-col md:grid md:gap-4 md:grid-cols-2 md:mt-6">
          <div class="md:max-w-xl md:max-h-96">
            <UCarousel
              v-if="rentUnit.media.length > 0"
              :items="useCottageMedia().getPhotoItems(rentUnit.media)"
              :ui="fullWidthCarousel"
              :dots="isMobile"
              :arrows="!isMobile"
              class="inset-0 overflow-hidden md:rounded-lg"
            >
              <template #default="media">
                <div class="w-full h-full aspect-w-3 aspect-h-2 m-auto">
                  <TLazyimg
                    :src="media.item.url"
                    classes-img="block w-full h-full object-cover"
                    :alt="media.item.alt"
                  />
                </div>
              </template>
            </UCarousel>
          </div>
          <div class="my-5 md:my-0 px-5 md:px-0">
            <TText size="lg" variant="semibold" classes="mb-0">{{
              t(`views.cottage-prices.cottage-types.${rentUnit.type}`)
            }}</TText>
            <TText size="lg">{{ rentUnit.name }}</TText>

            <TText classes="mb-0">
              <span class="font-bold">{{
                t('views.cottage-prices.detail.bathrooms')
              }}</span>
              {{ rentUnit.bathrooms }}
            </TText>
            <TText v-if="rentUnit.features.services.length > 0" classes="mb-0">
              <span class="font-bold">{{
                t('views.cottage-prices.detail.services')
              }}</span>
              {{ joinNames(rentUnit.features.services) }}
            </TText>
            <TText v-if="rentUnit.features.interiors.length > 0" classes="mb-0">
              <span class="font-bold">{{
                t('views.cottage-prices.detail.interior')
              }}</span>
              {{ joinNames(rentUnit.features.interiors) }}
            </TText>
            <TText
              v-if="
                rentUnit.features.accessibilities &&
                rentUnit.features.accessibilities.length > 0
              "
              classes="mb-0"
            >
              <span class="font-bold">{{
                t('views.cottage-prices.detail.accessibility')
              }}</span>
              {{ joinNames(rentUnit.features.accessibilities) }}
            </TText>
            <TText v-if="rentUnit.features.exteriors.length > 0" classes="mb-0">
              <span class="font-bold">{{
                t('views.cottage-prices.detail.exterior')
              }}</span>
              {{ joinNames(rentUnit.features.exteriors) }}
            </TText>
            <TText v-if="rentUnit.extras.length > 0" classes="mb-0">
              <span class="font-bold">{{
                t('views.cottage-prices.detail.extras.title')
              }}</span>
              {{ formatExtras(rentUnit.extras) }}
            </TText>
            <TText v-if="rentUnit.description" classes="mb-0">
              <span class="font-bold">{{
                t('views.cottage-prices.detail.description')
              }}</span>
              <span v-html="rentUnit.description" />
            </TText>
          </div>
        </div>
      </TContainer>
    </template>
  </UModal>
</template>
<script setup lang="ts">
import { useCottageMedia } from '~~/modules/views/cottage-prices/composables/useCottageMedia'
import { fullWidthCarousel } from '~~/modules/design-system/utils/nuxt-ui-config/components/carousel'
import type { schemaType as rentUnitsSchemaType } from '~~/modules/api/rurality/v3/cottages/rent-units-detail/schema.ts'

defineProps<{
  rentUnit: rentUnitsSchemaType['rentUnits'][number] | null
}>()

const { isMobile } = useDevice()

const modalIsOpen = defineModel<boolean>()

const joinNames = (
  items:
    | rentUnitsSchemaType['rentUnits'][number]['features']['services']
    | rentUnitsSchemaType['rentUnits'][number]['features']['interiors']
    | rentUnitsSchemaType['rentUnits'][number]['features']['accessibilities']
    | rentUnitsSchemaType['rentUnits'][number]['features']['exteriors'],
) => {
  if (items) {
    return (items as Array<{ name: string }>).map(item => item.name).join(', ')
  }
}

const formatExtras = (
  items: rentUnitsSchemaType['rentUnits'][number]['extras'],
) => {
  if (!items) return ''

  const typeToTranslationKey: Record<
    rentUnitsSchemaType['rentUnits'][number]['extras'][number]['type'],
    string
  > = {
    PEOPLE_DAY: 'views.cottage-prices.detail.extras.peopleDay',
    SINGLE_PAYMENT: 'views.cottage-prices.detail.extras.singlePayment',
    UNITY_DAY: 'views.cottage-prices.detail.extras.unityDay',
    UNITY: 'views.cottage-prices.detail.extras.unity',
  }

  return items
    .map(({ name, price, type }) => {
      const translationKey = typeToTranslationKey[type]
      const priceDescription = t(translationKey, {
        price: toEuro(price.amount),
      })
      return `${name} ${priceDescription}`
    })
    .join(', ')
}
</script>
